import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../utils/auth'
import { tenantApi } from '../../utils/api'

interface Camp {
  id: number
  name: string
  description: string
  startDate: string
  endDate: string
  ageMin: number
  ageMax: number
  maxSpots: number
  spotsTaken: number
  registrationStatus: string
}

export default function TenantHome() {
  const { slug } = useParams()
  const { tenant, loading: authLoading } = useAuth()
  const [camps, setCamps] = useState<Camp[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadCamps()
  }, [])

  const loadCamps = async () => {
    try {
      const data = await tenantApi.getCamps()
      setCamps(data.camps || [])
    } catch (err) {
      console.error('Failed to load camps:', err)
    } finally {
      setLoading(false)
    }
  }

  const basePath = slug ? `/t/${slug}` : ''

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Organization Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">This organization doesn't exist or has been deactivated.</p>
          <Link to="/home" className="btn-primary">Go to Camp Cloud</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {tenant.logoText || tenant.name.charAt(0)}
              </div>
            )}
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white">{tenant.name}</h1>
              {tenant.tagline && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{tenant.tagline}</p>
              )}
            </div>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to={`${basePath}/admin`} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm">
              Admin
            </Link>
          </nav>
        </div>
      </header>
      
      {/* Hero */}
      <section 
        className="py-16 px-6"
        style={{ 
          background: `linear-gradient(135deg, ${tenant.primaryColor}15 0%, transparent 50%)` 
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Camp Registration
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Register for upcoming camps and programs at {tenant.name}
          </p>
        </div>
      </section>
      
      {/* Camps List */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Camps</h3>
          
          {camps.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="text-4xl mb-4">🏕️</div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No camps available</h4>
              <p className="text-gray-600 dark:text-gray-400">
                Check back soon for upcoming programs
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {camps.map((camp) => {
                const spotsLeft = camp.maxSpots - camp.spotsTaken
                const isFull = spotsLeft <= 0
                const isPaused = camp.registrationStatus === 'paused'
                const isClosed = camp.registrationStatus === 'closed'
                
                return (
                  <div key={camp.id} className="card p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {camp.name}
                          </h4>
                          {isFull && <span className="badge-warning">Full</span>}
                          {isPaused && <span className="badge-warning">Paused</span>}
                          {isClosed && <span className="badge-error">Closed</span>}
                        </div>
                        
                        {camp.description && (
                          <p className="text-gray-600 dark:text-gray-400 mb-3">{camp.description}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            📅 {new Date(camp.startDate).toLocaleDateString()} - {new Date(camp.endDate).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            👶 Ages {camp.ageMin}-{camp.ageMax}
                          </span>
                          <span className="flex items-center gap-1">
                            👥 {spotsLeft > 0 ? `${spotsLeft} spots left` : 'No spots available'}
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        {isClosed ? (
                          <button disabled className="btn-secondary opacity-50 cursor-not-allowed">
                            Closed
                          </button>
                        ) : isPaused ? (
                          <button disabled className="btn-secondary opacity-50 cursor-not-allowed">
                            Registration Paused
                          </button>
                        ) : (
                          <Link
                            to={`${basePath}/register?campId=${camp.id}`}
                            className="btn-primary"
                            style={{ backgroundColor: tenant.primaryColor }}
                          >
                            {isFull ? 'Join Waitlist' : 'Register Now'}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-500 dark:text-gray-400">
          <p>© {new Date().getFullYear()} {tenant.name}. All rights reserved.</p>
          {tenant.contactEmail && (
            <p className="mt-2">
              Contact: <a href={`mailto:${tenant.contactEmail}`} className="hover:underline" style={{ color: tenant.primaryColor }}>{tenant.contactEmail}</a>
            </p>
          )}
        </div>
      </footer>
    </div>
  )
}


